import React, { useState } from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { Onboarding } from '@components/common/Onboarding'
import { PageLayout } from '@components/common/PageLayout'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { ButtonClean } from '@fourthwall/components'
import {
  MiniRecommendations,
  MiniRecommendationsSkeleton,
  useMiniRecommendationsData,
} from '@modules/Dashboard/modules/MiniRecommendations'

import { useCurrentShopQuery } from '../hooks/useCurrentShopQuery'
import { useHelpRecommendations, useRecommendations } from '../modules/Recommendations'
import { ArticleListSection } from './components/ArticleListSection'
import { Scheduler } from './components/Scheduler'
import { SectionTitle } from './components/SectionTitle'
import { useBlogPosts } from './hooks/useBlogPosts'
import { useHome } from './hooks/useHome'
import { useOnboarding } from './hooks/useOnboarding'
import { usePersonalizedRecommendations } from './hooks/usePersonalizedRecommendations'
import { HomeAnalytics } from './modules/HomeAnalytics'
import * as S from './styled'

export const Home = () => {
  const { name } = useHome()
  const { onboarding } = useOnboarding()
  const { hasPermission } = useDashboardPermissions()
  const { currentShopQuery } = useCurrentShopQuery()
  const { recommendations } = useRecommendations()
  const { blogPosts } = useBlogPosts()
  const { helpRecommendations } = useHelpRecommendations()
  const { isLoading: isMiniRecommendationsLoading, data: miniRecommendationsData } =
    useMiniRecommendationsData()

  const [showAllRecommendations, setShowAllRecommendations] = useState(false)

  const { personalizedRecommendations } = usePersonalizedRecommendations(recommendations)

  const visibleRecommendations = showAllRecommendations
    ? personalizedRecommendations
    : personalizedRecommendations.slice(0, 6)
  const hasMoreRecommendations = personalizedRecommendations.length > visibleRecommendations.length

  const content = (['ONBOARDING_MEMBERSHIPS', 'NONE'].includes(onboarding.onboardingType) ||
    !!currentShopQuery.data?.lastLiveAt) &&
    hasPermission('home.analytics') && (
      <S.Spacing $space="400">
        <HomeAnalytics />
      </S.Spacing>
    )

  return (
    <PageLayout>
      <Dashboard.Section name={name}>
        <S.Spacing $space="1200">
          {content}

          {onboarding.schedulerProps && <Scheduler {...onboarding.schedulerProps} />}

          {onboarding.onboardingProps && <Onboarding {...onboarding.onboardingProps} />}

          <article>
            {onboarding.schedulerProps || onboarding.onboardingProps ? (
              <SectionTitle title="Next steps" />
            ) : null}

            <S.MiniRecommendations>
              {isMiniRecommendationsLoading ? (
                <MiniRecommendationsSkeleton />
              ) : (
                <MiniRecommendations data={miniRecommendationsData} page="home" />
              )}
            </S.MiniRecommendations>

            {visibleRecommendations.length > 0 && (
              <S.Recommendations>
                {visibleRecommendations.map(({ suggestion, id }) => {
                  return (
                    <S.SuggestionWrapper key={`recommendation-${id}`} data-analytics-section={id}>
                      {suggestion}
                    </S.SuggestionWrapper>
                  )
                })}

                {hasMoreRecommendations && (
                  <S.ShowMoreRecommendationsButton>
                    <ButtonClean
                      label="Show more recommendations"
                      withUnderline
                      appearance="secondary"
                      onClick={() => setShowAllRecommendations(true)}
                    />
                  </S.ShowMoreRecommendationsButton>
                )}
              </S.Recommendations>
            )}
          </article>

          {helpRecommendations.length > 0 && (
            <article>
              <SectionTitle title="Help & support" />

              <S.Recommendations>
                {helpRecommendations.map(({ suggestion, id }) => {
                  return (
                    <S.SuggestionWrapper
                      key={`help-recommendation-${id}`}
                      data-analytics-section={id}
                    >
                      {suggestion}
                    </S.SuggestionWrapper>
                  )
                })}
              </S.Recommendations>
            </article>
          )}

          {blogPosts && <ArticleListSection {...blogPosts} />}
        </S.Spacing>
      </Dashboard.Section>
    </PageLayout>
  )
}
